/**
 * AuthContext - Provides authentication state throughout the app
 *
 * Wraps the useAuth hook to provide global auth state and actions.
 * Supports both Magic Link (email) and Google Sign-In authentication.
 */
import { createContext, useContext, ReactNode } from 'react';
import { User } from 'firebase/auth';
import { useAuth } from '../hooks/useAuth';

type AuthStep = 'idle' | 'email-sent' | 'completing' | 'needs-email' | 'google-pending';

interface AuthContextType {
  // State
  user: User | null;
  loading: boolean;
  error: string | null;
  step: AuthStep;
  sentToEmail: string | null;
  
  // Actions - Magic Link
  sendLink: (email: string) => Promise<void>;
  completeSignIn: (email?: string) => Promise<void>;
  
  // Actions - Google Sign-In
  signInWithGoogle: () => Promise<void>;
  
  // Actions - Common
  signOut: () => Promise<void>;
  resetFlow: () => void;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();

  return (
    <AuthContext.Provider value={auth}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuthContext(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
}
